import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import cloneDeep from 'lodash/cloneDeep';
import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import set from 'lodash/set';

import { unescapeHTML } from 'src/actions/sidekick/utils';
import { toastError } from 'src/actions/toast';
import generateId from 'src/lib/generateId';
import SidekickModuleSpec, { ExternalAction, TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import SidekickThread, { ModifierValue, NextData } from 'src/models/Sidekick/SidekickThreads';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import { Line } from 'src/presentation/components/pages/sidekick/threads/components/Connectors/ConnectorsProvider';
import { checkIfBtResourcesDeleted } from './btResourcesActions';
import {
  convertBTreeResourceToModuleSpec,
  convertExternalActionToModuleSpec,
} from './convertExternalActionToModuleSpec';
import { updateExternalActionsIfNeeded } from './externalActionActions';
import { addModifier, getThreadsHierarchy, sendCardAction } from './moduleHelper';
import { navigateTo } from './navigationActions';
import { validateThread } from './threadActions';
import { getTypeSpecs } from './typeSpecsActions';
import {
  createNewModule,
  definedMemoriesAndExActionNames,
  flattenSteps,
  getNestedModules,
  NestedModuleOrModifier,
  NodesTypes,
} from './utils';

const parseQueryString = (query = '') => {
  const params = {};
  const queryString = query.substring(1);
  const pairs = queryString.split('&');
  pairs.forEach(pair => {
    const [key, value] = pair.split('=');
    params[key] = value;
  });
  return params;
};

// Function to initialize route ID from window location
const initializeRouteIdFromLocation = () => {
  const { search } = window.location;
  const queryParams = parseQueryString(search || '') as Record<string, string>;

  return {
    moduleIndex: !isUndefined(queryParams?.moduleIndex) ? parseInt(queryParams.moduleIndex) : null,
    modifierIndex: !isUndefined(queryParams?.modifierIndex) ? parseInt(queryParams.modifierIndex) : null,
    anon: queryParams?.anon === 'true' || false,
    revisionId: queryParams?.revisionId ? parseInt(queryParams.revisionId) : null,
    anonModifierIndex: !isUndefined(queryParams?.anonModifierIndex) ? parseInt(queryParams.anonModifierIndex) : null,
  };
};

const initialState = {
  threads: [] as SidekickThread[],
  currentThread: null as SidekickThread | null,
  moduleIndex: initializeRouteIdFromLocation().moduleIndex,
  anonModifierIndex: initializeRouteIdFromLocation().anonModifierIndex,
  modifierIndex: initializeRouteIdFromLocation().modifierIndex as number | null,
  anon: initializeRouteIdFromLocation().anon as boolean,
  currentMemories: [] as string[],
  moduleSpecsMap: {} as Record<string, SidekickModuleSpec>,
  typeSpecs: {} as Record<string, TypeSpec>,
  extActionMemNames: [] as string[],
  errors: [] as { attr: string; code: string; detail: string }[],
  isDirty: false,
  cache: {} as Record<string, Record<string, unknown>>,
  lastCreatedThreadId: null as number | null,
  revisionId: null as number | null,
  threadsHierarchy: {} as Record<string, string[]>,
  lastSavedRevision: null as Date | null,
  externalActionsErrors: [] as { attr: string; code: string; detail: string }[],

  currentModulesState: {} as Record<string, { isOpen: boolean; isSelected: boolean }>,
  lines: {} as Record<string, Line>,
  // threadsParentsMap: {} as Record<string, string[]>,
  showLoadingIndicator: false,
  initialThreadState: null as SidekickThread | null,

  showDisableThreadNotAllowedModal: false,
  childThreads: [] as number[],
  showLoadingModal: false,
};

const threadUiSlice = createSlice({
  name: 'threadUi',
  initialState,
  reducers: {
    setThreads: (state, action) => {
      state.threads = action.payload;
    },
    setModuleIndex: (state, action) => {
      state.moduleIndex = action.payload;
    },
    setCurrentThread: (state, action) => {
      state.currentThread = action.payload;
    },
    setCurrentMemories: (state, action) => {
      state.currentMemories = action.payload;
    },
    setModifierIndex: (state, action) => {
      state.modifierIndex = action.payload;
    },
    setAnonModifierIndex: (state, action) => {
      state.anonModifierIndex = action.payload;
    },

    setModuleSpecsMap: (state, action) => {
      state.moduleSpecsMap = action.payload;
    },
    setTypeSpecs: (state, action) => {
      state.typeSpecs = action.payload;
    },
    setExtActionMemNames: (state, action) => {
      state.extActionMemNames = action.payload;
    },
    setAnon(state, action) {
      state.anon = action.payload;
    },
    setIsDirty: (state, action) => {
      state.isDirty = action.payload;
    },
    setErrors: (state, action) => {
      state.errors = action.payload;
    },
    setCache: (state, action) => {
      state.cache = action.payload;
    },
    setLastCreatedThreadId(state, action) {
      state.lastCreatedThreadId = action.payload;
    },
    setRevisionId(state, action) {
      state.revisionId = action.payload;
    },
    setThreadsHierarchy(state, action) {
      state.threadsHierarchy = action.payload;
    },
    setLastSavedRevision(state, action) {
      state.lastSavedRevision = action.payload;
    },
    setCurrentModulesState(state, action) {
      state.currentModulesState = action.payload;
    },
    setLines(state, action) {
      const lines = cloneDeep(state.lines) as any;
      const name = Object.keys(action.payload)[0];

      lines[name] = action.payload[name];
      state.lines = lines;
    },
    cleanExternalActionsErrors(state) {
      state.externalActionsErrors = [];
    },
    setExternalActionsErrors(state, action) {
      let errors = cloneDeep(state.externalActionsErrors);

      if (Array.isArray(action.payload)) {
        errors = [...errors, ...action.payload];
      } else {
        errors.push(action.payload);
      }

      state.externalActionsErrors = errors;
    },
    // setThreadsParents(state, action) {
    //   state.threadsParentsMap = action.payload;
    // },
    setShowLoadingIndicator(state, action) {
      state.showLoadingIndicator = action.payload;
    },
    setInitialThreadState(state, action) {
      state.initialThreadState = action.payload;
    },
    setShowDisableThreadNotAllowedModal(state, action) {
      state.showDisableThreadNotAllowedModal = action.payload;
    },
    setChildThreads(state, action) {
      state.childThreads = action.payload;
    },
    setShowLoadingModal(state, action) {
      state.showLoadingModal = action.payload;
    },
  },
});

export const {
  cleanExternalActionsErrors,
  setAnon,
  setAnonModifierIndex,
  setCache,
  setCurrentMemories,
  setCurrentModulesState,
  setCurrentThread,
  setErrors,
  setExtActionMemNames,
  setExternalActionsErrors,
  setIsDirty,
  setLastCreatedThreadId,
  setLastSavedRevision,
  setLines,
  setModifierIndex,
  setModuleIndex,
  setModuleSpecsMap,
  setRevisionId,
  setThreads,
  setThreadsHierarchy,
  setTypeSpecs,
  setShowLoadingIndicator,
  setInitialThreadState,
  setShowDisableThreadNotAllowedModal,
  setChildThreads,
  setShowLoadingModal,
} = threadUiSlice.actions;

export const transformAndSetThreads = createAsyncThunk(
  'threadUi/transformAndSetThreads',
  async (threads: SidekickThread[], { dispatch, getState }) => {
    const transformedThreads = threads.map(thread => ({
      ...thread,
      updatedAtString: toDateTimeString(thread.updatedAt),
    }));
    dispatch(setThreads(transformedThreads));

    const currentThread = selectCurrentThread(getState());
    if (currentThread) {
      const changedThread = transformedThreads.find(thread => thread.id === currentThread.id);
      if (!changedThread) {
        return;
      }

      dispatch(changeCurrentThread({ ...changedThread, nextData: currentThread.nextData }));
    }
  }
);

const getId = (object: { id?: string }, force?: boolean) => (force || !object.id ? generateId() : object.id);

export const transformThread = (thread: SidekickThread, forceToChangeId?: boolean) => {
  const nextData = map(cloneDeep(thread.nextData) as NextData[], (data: NextData) => {
    const modifierValues = map(data.modifierValues, (value: ModifierValue) => {
      const anonModule = value.controlValues.rule?.then?.anonymousModule;
      if (value.name === 'rule' && anonModule) {
        anonModule.id = getId(anonModule, forceToChangeId);
        const modifierValues = map(anonModule.modifierValues, (value: ModifierValue) => ({
          ...value,
          id: getId(value, forceToChangeId),
        }));
        anonModule.modifierValues = modifierValues;
      }
      return { ...value, id: getId(value, forceToChangeId) };
    });
    return { ...data, id: getId(data, forceToChangeId), modifierValues };
  });
  return { ...thread, nextData } as any;
};

export const mutateModifierIfNeeded = createAsyncThunk(
  'threadUi/mutateModifierIfNeeded',
  async (modifier: ModifierValue, { dispatch, getState }: any) => {
    const { value: appFeatures } = getState().appFeatures;

    if (modifier.name === 'addTopics' && !!appFeatures?.sidekickThreadUseTopicIds) {
      const tags = modifier.controlValues?.tags || '';

      const topicNames = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);

      if (!topicNames.length && !modifier.controlValues.useTopicIds) {
        const clonedModifier = cloneDeep(modifier);
        clonedModifier.controlValues.useTopicIds = true;
        await dispatch(updateCurrentModule({ moduleData: clonedModifier }));
        await dispatch(checkIsDirty());
        await dispatch(validateThread());
      }

      if (topicNames.length && !modifier.controlValues.useTopicIds) {
        const { values: rawTopics } = getState().topics;
        const enabledTopics = rawTopics.filter(topic => topicNames.includes(topic.name) && !topic.disabled);

        // replace topic names with topic ids only if all topics are found
        if (topicNames.length === enabledTopics.length) {
          const clonedModifier = cloneDeep(modifier);
          clonedModifier.controlValues.tags = enabledTopics.map(topic => topic.id).join(',');
          clonedModifier.controlValues.useTopicIds = true;

          await dispatch(updateCurrentModule({ moduleData: clonedModifier }));
          await dispatch(checkIsDirty());
          await dispatch(validateThread());
        } else if (isNil(modifier.controlValues.useTopicIds)) {
          // if useTopicIds is not set, set it to false
          const clonedModifier = cloneDeep(modifier);
          clonedModifier.controlValues.useTopicIds = false;
          await dispatch(updateCurrentModule({ moduleData: clonedModifier }));
          await dispatch(checkIsDirty());
          await dispatch(validateThread());
        }
      }
    }
  }
);

export const setCurrentThreadId = createAsyncThunk(
  'threadUi/setCurrentThreadId',
  async (threadId: string, { dispatch, getState, extra: { gateways } }: any) => {
    const state = getState() as unknown as { threadUi };
    const customerId = selectCustomerId(getState());

    const { currentThreadId } = state.threadUi;

    if (!currentThreadId || currentThreadId !== threadId) {
      dispatch(clearThreadId());
    }
    const thread = await gateways.sidekickThreads.fetch({ id: threadId, customerId });

    const currentThread = unescapeHTML(thread.data);

    await dispatch(changeCurrentThread(transformThread(currentThread)));
    await dispatch(setInitialThreadState(currentThread));

    await dispatch(computeThreadsHierarchy());

    await dispatch(computeModuleSpecsMap());

    await dispatch(getTypeSpecs());
    await dispatch(changeCurrentMemories({ moduleIndex: 0, modifierIndex: -1 }));

    await dispatch(cleanExternalActionsErrors());
    await dispatch(updateExternalActionsIfNeeded({ forceUpdate: true }));
    await dispatch(checkIfBtResourcesDeleted());
    await dispatch(validateThread());
    dispatch(checkIsDirty());
  }
);

export const checkIsDirty = createAsyncThunk('threadUi/checkIsDirty', async (_, { dispatch, getState }) => {
  const currentThread = selectCurrentThread(getState());
  const initialThreadState = (getState() as { threadUi }).threadUi.initialThreadState;
  const isDirty = !isEqual(unescapeHTML(currentThread.nextData), initialThreadState.nextData);

  dispatch(setIsDirty(isDirty));
});

export const changeCurrentThread = createAsyncThunk(
  'threadUi/changeCurrentThread',
  async (thread: SidekickThread | null, { dispatch, getState }) => {
    if (thread) {
      const currentModulesState = cloneDeep(selectCurrentModulesState(getState()));
      const selectedModule = selectCurrentModule(getState());

      const mods = flattenSteps(thread.nextData).filter(mod => mod.nodeType === NodesTypes.MODULE);
      mods.forEach(({ mod, attr }) => {
        if (!currentModulesState[mod.id!]) {
          currentModulesState[mod.id!] = { isOpen: true, isSelected: selectedModule?.id === mod.id, attr };
        }
      });

      await dispatch(setCurrentModulesState(currentModulesState));
      await dispatch(setCurrentThread(thread));
      return;
    }

    await dispatch(setCurrentModulesState({}));
    await dispatch(setCurrentThread(thread));
  }
);

export const clearThreadId = createAsyncThunk('threadUi/clearThreadId', async (_, { dispatch }) => {
  dispatch(setModuleIndex(0));
  dispatch(setModifierIndex(null));
  dispatch(setAnon(false));
  dispatch(setAnonModifierIndex(null));
  dispatch(changeCurrentThread(null));
  dispatch(setLastCreatedThreadId(null));
  dispatch(setErrors([]));
  dispatch(setCache({}));
  dispatch(cleanExternalActionsErrors());
});

export const deleteModuleOrModifier = createAsyncThunk(
  'threadUi/deleteModuleOrModifier',
  async (
    {
      moduleId,
      modifierId,
      anonModifierId,
      history,
    }: { moduleId: string; modifierId?: string; anonModifierId?: string; history },
    { dispatch, getState }
  ) => {
    const currentThread = cloneDeep(selectCurrentThread(getState()));

    const moduleIndex = currentThread.nextData.findIndex(data => data.id === moduleId);
    if (moduleIndex === -1) {
      return;
    }

    if (!modifierId) {
      currentThread.nextData.splice(moduleIndex, 1);
      await dispatch(updateThread({ thread: currentThread }));

      await dispatch(changeModuleIndex({ moduleIndex: 0, history, modifierIndex: null }));
      return;
    } else if (anonModifierId) {
      const modifierIndex = currentThread.nextData[moduleIndex].modifierValues.findIndex(
        modifier => modifier.id === modifierId
      );

      const anonModule =
        currentThread.nextData[moduleIndex].modifierValues[modifierIndex].controlValues.rule.then.anonymousModule;
      if (!anonModule) {
        return;
      }
      const anonModifierIndex = anonModule.modifierValues.findIndex(modifier => modifier.id === anonModifierId);
      if (anonModifierIndex === -1) {
        return null;
      }

      anonModule.modifierValues.splice(anonModifierIndex, 1);
      anonModule.id = generateId(); // to force re-render

      await dispatch(updateThread({ thread: currentThread }));
      await dispatch(changeModuleIndex({ moduleIndex: 0, history, modifierIndex: null }));
    } else {
      const modifierIndex = currentThread.nextData[moduleIndex].modifierValues.findIndex(
        modifier => modifier.id === modifierId
      );

      if (modifierIndex === -1) {
        return;
      }
      currentThread.nextData[moduleIndex].modifierValues.splice(modifierIndex, 1);
      currentThread.nextData[moduleIndex].id = generateId(); // to force re-render

      await dispatch(updateThread({ thread: currentThread }));
      dispatch(changeModuleIndex({ moduleIndex, history, modifierIndex: null }));
    }
  }
);

export const reorderModules = createAsyncThunk(
  'threadUi/dragModule',
  async (
    { source, destination }: { source: { index: number }; destination: { index: number } },
    { dispatch, getState }
  ) => {
    const currentThread = cloneDeep(selectCurrentThread(getState()));

    const newOptions = Array.from(currentThread.nextData) as NextData[];
    const [removed] = newOptions.splice(source.index, 1);
    newOptions.splice(destination.index, 0, removed);

    currentThread.nextData = newOptions;

    await dispatch(updateThread({ thread: currentThread }));
    dispatch(setModuleIndex(destination.index));
  }
);

export const reorderModifiers = createAsyncThunk(
  'threadUi/dragModifier',
  async (
    { source, destination }: { source: { index: number }; destination: { index: number; droppableId: number } },
    { dispatch, getState }
  ) => {
    const currentThread = cloneDeep(selectCurrentThread(getState()));

    const anon = selectAnon(getState());
    if (anon) {
      const mods = flattenSteps(currentThread.nextData);

      const anonModifier = selectCurrentAnonModifier(getState());
      const anonMod = find(mods, mod => mod.mod.id === anonModifier.id) as NestedModuleOrModifier;

      const parentArr = anonMod.attr!.split('.');
      parentArr.pop();
      const parentAttr = `${parentArr.join('.')}.modifierValues`;

      const modifiers = get(currentThread.nextData, parentAttr, []);

      const newModifiers = Array.from(modifiers);
      const [removed] = newModifiers.splice(source.index, 1);
      newModifiers.splice(destination.index, 0, removed);

      currentThread.nextData = set(currentThread.nextData, parentAttr, newModifiers);

      dispatch(updateThread({ thread: currentThread }));

      return;
    }

    const moduleIndex = currentThread.nextData.findIndex(data => data.id === destination.droppableId);
    if (moduleIndex === -1) {
      return;
    }

    const module = { ...currentThread.nextData[moduleIndex] };

    const newModifiers = Array.from(module.modifierValues);
    const [removed] = newModifiers.splice(source.index, 1);
    newModifiers.splice(destination.index, 0, removed);

    module.modifierValues = newModifiers;

    currentThread.nextData[moduleIndex] = module;

    dispatch(updateThread({ thread: currentThread }));
  }
);

export const changeModuleIndex = createAsyncThunk(
  'threadUi/changeRouteIdAsync',
  async ({ moduleIndex, history, modifierIndex }: { moduleIndex; history; modifierIndex }, { dispatch, getState }) => {
    dispatch(setShowLoadingIndicator(true));
    try {
      const {
        anon: priorAnon,
        modifierIndex: priorModifierIndex,
        moduleIndex: priorModuleIndex,
      } = (getState() as unknown as { threadUi }).threadUi;

      const anon = priorAnon;
      const anonModifierIndex = anon && !isNil(modifierIndex) ? modifierIndex : null;

      const currentModifierIndex = anon ? priorModifierIndex : modifierIndex;
      const currentModuleIndex = anon ? priorModuleIndex : moduleIndex;

      if (!isNil(modifierIndex)) {
        // mutate current modifier (if needed...)
        const currentThread = selectCurrentThread(getState());
        const currentModule = getCurrentModule(currentThread, currentModuleIndex);
        let modifier: ModifierValue = currentModule.modifierValues[currentModifierIndex];
        if (anon) {
          modifier = get(modifier, `controlValues.rule.then.anonymousModule.modifierValues.${modifierIndex}`, null);
        }

        await dispatch(mutateModifierIfNeeded(modifier));
      }

      if (anon) {
        await dispatch(setAnonModifierIndex(anonModifierIndex));
      } else {
        await dispatch(setModifierIndex(modifierIndex));
        await dispatch(setModuleIndex(moduleIndex));
      }

      await dispatch(
        changeCurrentMemories({
          moduleIndex: currentModuleIndex,
          modifierIndex: currentModifierIndex !== undefined ? currentModifierIndex : -1,
        })
      );

      await dispatch(navigateTo({ history }));
    } catch (err) {
      // do nothing
    } finally {
      dispatch(setShowLoadingIndicator(false));
    }
  }
);

export const computeModuleSpecsMap = createAsyncThunk(
  'threadUi/computeTypeSpecs',
  async (_, { dispatch, getState }) => {
    const state = getState() as unknown as {
      sidekickBTreeResources;
      sidekickExternalActions;
      sidekickModuleSpecs;
      threadUi;
    };

    const extActions = state.sidekickExternalActions.values as ExternalAction[];
    const { values: moduleSpecs } = state.sidekickModuleSpecs;

    const bTreeResources = state.sidekickBTreeResources.values || [];

    const extActionsModuleSpecs = extActions.map(convertExternalActionToModuleSpec);
    const bTreeResourcesModuleSpecs = bTreeResources.map(convertBTreeResourceToModuleSpec);
    const specsWithExtActions = [...moduleSpecs, ...extActionsModuleSpecs, ...bTreeResourcesModuleSpecs];

    const specsMap = reduce(specsWithExtActions, (acc, spec) => ({ ...acc, [spec.name]: spec }), {}) as Record<
      string,
      SidekickModuleSpec
    >;

    if (!specsMap.sendCard) {
      specsMap.sendCard = sendCardAction as SidekickModuleSpec;
    }

    dispatch(setModuleSpecsMap(specsMap));
  }
);

export const updateCurrentModule = createAsyncThunk(
  'threadUi/updateCurrentModule',
  async ({ moduleData }: { moduleData: NextData | ModifierValue }, { dispatch, getState }) => {
    const currentThread = cloneDeep(selectCurrentThread(getState()));

    const mods = flattenSteps(currentThread.nextData);
    const moduleOrModifierAttr = find(mods, mod => mod.mod.id === moduleData.id)?.attr as string;

    currentThread.nextData = set(currentThread.nextData, moduleOrModifierAttr, moduleData);
    dispatch(updateThread({ thread: currentThread }));
  }
);

export const changeCurrentMemories = createAsyncThunk(
  'threadUi/changeCurrentMemories',
  async ({ moduleIndex, modifierIndex }: { moduleIndex: number; modifierIndex: number }, { dispatch, getState }) => {
    const { value: customer } = (getState() as unknown as { sidekickCustomer }).sidekickCustomer;
    const currentThread = selectCurrentThread(getState());
    const specsMap = selectModuleSpecsMap(getState());

    let nestedModules = getNestedModules(moduleIndex, modifierIndex, currentThread.nextData, specsMap);
    const types = cloneDeep(selectTypeSpecs(getState()));

    const currentModule = selectCurrentModule(getState());

    // For external actions - We don't want to include the memory defined in the current module,
    // so we start with the previous module index.
    if (currentModule?.externalAction) {
      nestedModules = nestedModules.slice(0, nestedModules.length - 1);
    }

    try {
      const [memories, extActionMemNames] = definedMemoriesAndExActionNames(
        nestedModules,
        currentThread.name,
        customer,
        specsMap,
        types // it's mutating the types object
      );

      dispatch(setExtActionMemNames(extActionMemNames));
      dispatch(setTypeSpecs(types));
      dispatch(setCurrentMemories(memories));
    } catch (err) {
      // TODO: show error message
      // ignore error
      // console.error(err);
    }
  }
);

export const addModifierToModule = createAsyncThunk(
  'threadUi/addModifierToModule',
  async (
    {
      modifierName,
      moduleName,
      moduleId,
      history,
    }: { modifierName: string; moduleName: string; moduleId: string; history },
    { dispatch, getState }
  ) => {
    const { value: appFeatures } = (getState() as { appFeatures }).appFeatures;
    const currentThread = cloneDeep(selectCurrentThread(getState()));

    const mods = flattenSteps(currentThread.nextData);
    const module = find(mods, mod => mod.mod.id === moduleId)?.mod as NextData;

    const specs = selectModuleSpecsMap(getState());
    const moduleSpec = specs[moduleName];

    const modifierSpec = moduleSpec.modifiers.find(modifier => modifier.name === modifierName);
    if (!modifierSpec) {
      return;
    }

    if (!module) {
      const anonModule = cloneDeep(selectCurrentAnonModule(getState()));
      if (!anonModule) {
        return;
      }

      const currentModifier = selectCurrentModifier(getState());
      const modifier = mods.find(mod => mod.mod.id === currentModifier.id)?.mod as ModifierValue | undefined;

      if (!modifier) {
        return;
      }

      addModifier(undefined, anonModule, modifierSpec, { appFeatures });
      modifier.controlValues.rule.then.anonymousModule = anonModule;

      dispatch(updateThread({ thread: currentThread }));

      return;
    }

    addModifier(undefined, module, modifierSpec, { appFeatures });
    await dispatch(updateThread({ thread: currentThread }));

    const moduleIndex = currentThread.nextData.findIndex(data => data.id === moduleId);

    await dispatch(toggleModuleExpandCollapse({ moduleId, isOpen: true }));

    dispatch(changeModuleIndex({ moduleIndex, history, modifierIndex: module.modifierValues.length - 1 }));
  }
);

export const addModuleToCurrentThread = createAsyncThunk(
  'threadUi/addModuleToCurrentThread',
  async (
    { moduleName, aboveModuleId, history }: { moduleName: string; aboveModuleId: string | null; history },
    { dispatch, getState }
  ) => {
    try {
      const specs = selectModuleSpecsMap(getState());

      const currentThread = cloneDeep(selectCurrentThread(getState()));

      const { values: sidekickIntegrations } = (getState() as { sidekickIntegrations }).sidekickIntegrations;

      const newModule = createNewModule(moduleName, specs, sidekickIntegrations?.data || {}) as Partial<NextData>;

      const aboveModuleIndex = aboveModuleId
        ? currentThread.nextData.findIndex(data => data.id === aboveModuleId) + 1
        : 0;

      const nextData = [...currentThread.nextData];
      nextData.splice(aboveModuleIndex, 0, newModule);

      currentThread.nextData = nextData;

      await dispatch(updateThread({ thread: currentThread }));
      await dispatch(changeModuleIndex({ moduleIndex: aboveModuleIndex, history, modifierIndex: null }));
    } catch (err) {
      Sentry.captureException(err);
      dispatch(toastError('There was an error while add module.') as any);
    }
  }
);

export const updateThread = createAsyncThunk(
  'threadUi/updateThread',
  async ({ thread }: { thread: SidekickThread }, { dispatch }) => {
    await dispatch(changeCurrentThread(thread));

    dispatch(checkIsDirty());
    dispatch(validateThread());
    dispatch(computeThreadsHierarchy());
  }
);

export const computeThreadsHierarchy = createAsyncThunk(
  'threadUi/computeThreadsHierarchy',
  async (_, { dispatch, getState }) => {
    const { threads, currentThreadId, currentThread } = (getState() as { threadUi }).threadUi;
    const threadsWithoutCurrent = threads.filter(thread => thread.id !== currentThreadId);
    dispatch(setThreadsHierarchy(getThreadsHierarchy([...threadsWithoutCurrent, currentThread])));
  }
);

const getCurrentModule = (currentThread, moduleIndex) => {
  if (!isUndefined(moduleIndex)) {
    if (!currentThread?.nextData) {
      return null;
    }
    return currentThread.nextData[moduleIndex];
  }
  return null;
};

const getCurrentModifier = (currentThread, moduleIndex, modifierIndex) => {
  const module = getCurrentModule(currentThread, moduleIndex);
  if (!module) {
    return null;
  }
  return module.modifierValues[modifierIndex];
};

const getCurrentAnonModifier = (currentThread, moduleIndex, modifierIndex, anon, anonModifierIndex) => {
  const modifier = getCurrentModifier(currentThread, moduleIndex, modifierIndex);
  if (!modifier) {
    return null;
  }

  if (!anon) {
    return modifier;
  }

  const anonymousModule = get(modifier, 'controlValues.rule.then.anonymousModule', null);
  if (!anonymousModule) {
    return null;
  }

  return anonymousModule.modifierValues[anonModifierIndex];
};

const getCurrentAnonModule = (currentThread, moduleIndex, modifierIndex) => {
  const modifier = getCurrentModifier(currentThread, moduleIndex, modifierIndex);

  const anonymousModule = get(modifier, 'controlValues.rule.then.anonymousModule', null);
  if (!anonymousModule) {
    return null;
  }
  return anonymousModule;
};

export const selectCurrentModule = createSelector(
  [state => state.threadUi?.currentThread, state => state.threadUi?.moduleIndex],
  getCurrentModule
);

export const selectCurrentModifier = createSelector(
  [
    state => state.threadUi?.currentThread,
    state => state.threadUi?.moduleIndex,
    state => state.threadUi?.modifierIndex,
  ],
  getCurrentModifier
);

export const selectCurrentAnonModifier = createSelector(
  [
    state => state.threadUi?.currentThread,
    state => state.threadUi?.moduleIndex,
    state => state.threadUi?.modifierIndex,
    state => state.threadUi?.anon,
    state => state.threadUi?.anonModifierIndex,
  ],
  getCurrentAnonModifier
);

export const selectCurrentAnonModule = createSelector(
  [
    state => state.threadUi?.currentThread,
    state => state.threadUi?.moduleIndex,
    state => state.threadUi?.modifierIndex,
  ],
  getCurrentAnonModule
);

export const toggleModuleExpandCollapse = createAsyncThunk(
  'threadUi/toggleModuleExpandCollapse',
  async ({ moduleId, isOpen }: { moduleId: string; isOpen?: boolean }, { dispatch, getState }) => {
    if (!moduleId) {
      return;
    }
    const currentModulesState = cloneDeep(selectCurrentModulesState(getState()));
    currentModulesState[moduleId].isOpen = isOpen === undefined ? !currentModulesState[moduleId].isOpen : isOpen;

    dispatch(setCurrentModulesState(currentModulesState));
  }
);

export const selectCurrentMemories = state => state.threadUi.currentMemories;
export const selectModuleIndex = state => state.threadUi?.moduleIndex;
export const selectModifierIndex = state => state.threadUi?.modifierIndex;

export const selectCustomerId = state => state.sidekickConfiguration.value.v1CustomerId;

export const selectCurrentThread = state => state.threadUi?.currentThread;

export const selectModuleSpecsMap = state => state.threadUi?.moduleSpecsMap;
export const selectTransformedThreads = state => state.threadUi?.threads;
export const selectTypeSpecs = state => state.threadUi.typeSpecs;
export const selectExtActionMemNames = state => state.threadUi.extActionMemNames;
export const selectAnon = state => state.threadUi.anon;

export const selectAnonModifierIndex = state => state.threadUi.anonModifierIndex;

export const selectIsDirty = state => state.threadUi.isDirty;
export const selectErrors = state => state.threadUi.errors;
export const selectCache = state => state.threadUi.cache;
export const selectLastCreatedThreadId = state => state.threadUi.lastCreatedThreadId;

export const selectCurrentThreadParents = createSelector(
  [state => state.threadUi?.currentThread, state => state.threadUi.threadsHierarchy, state => state.threadUi.threads],
  (currentThread, threadsHierarchy, threads) => {
    if (!currentThread) {
      return [];
    }

    const parentIds = Object.keys(threadsHierarchy).filter(
      parentId => threadsHierarchy[parentId] && threadsHierarchy[parentId].includes(currentThread.id)
    );

    return threads
      .filter(thread => parentIds.includes(String(thread.id)))
      .map(thread => ({ id: thread.id, name: thread.name }));
  }
);

export const selectLastSavedRevision = state => state.threadUi.lastSavedRevision;
export const selectCurrentModulesState = state => state.threadUi.currentModulesState;
export const selectExternalActionsErrors = state => state.threadUi.externalActionsErrors;

export const selectThreadMeta = createSelector(
  [state => state.sidekickFastThreads.values, state => state.threadUi.currentThread],
  (threads, currentThread) => threads.find(thread => thread.id === currentThread.id)
);

export const selectLines = state => state.threadUi.lines;
export const selectShowLoadingIndicator = state => state.threadUi.showLoadingIndicator;
export const selectShowDisableThreadNotAllowedModal = state => state.threadUi.showDisableThreadNotAllowedModal;
export const selectChildThreads = state => state.threadUi.childThreads;
export const selectShowLoadingModal = state => state.threadUi.showLoadingModal;

export default threadUiSlice;
